import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import NotFoundError from 'src/components/ErrorBoundary/PlaceholderErrorBoundary/NotFoundError';
import Breadcrumbs from 'src/enosikit/components/Breadcrumbs';
import usePortfolioUpdateMutation from 'src/mutations/PortfolioUpdateMutation';
import FlashesStore from 'src/stores/FlashesStore';

import Form from './Form';

const AdminPortfolioEditFragment = graphql`
  fragment AdminPortfolioEdit_portfolio on Portfolio @throwOnFieldError {
    uuid
    title
  }
`;

/**
 * Admin form for renaming a portfolio. Field errors, and a resolved query
 * without a portfolio, throw to the route's PlaceholderErrorBoundary.
 * @param {object} props - Component props.
 * @param {object|null} props.portfolio - Relay fragment reference for the portfolio.
 * @param {object} props.router - Found router, used to return to the portfolio after saving.
 * @returns {React.ReactComponentElement} AdminPortfolioEdit component.
 */
function AdminPortfolioEdit({ portfolio: portfolioRef = null, router }) {
  const portfolio = useFragment(AdminPortfolioEditFragment, portfolioRef);
  const [processing, setProcessing] = useState(false);
  const intl = useIntl();
  const editPortfolio = usePortfolioUpdateMutation();

  if (!portfolio) {
    // A completed query without a portfolio would otherwise never leave the spinner.
    throw new NotFoundError('AdminPortfolioEdit: the portfolio query returned no portfolio');
  }

  const { title: portfolioTitle, uuid: portfolioUuid } = portfolio;
  const pageTitle = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_edit.page.title', defaultMessage: 'Enosi Admin - Portfolio Edit' });
  const breadcrumbAdmin = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_edit.breadcrumbs.admin.label', defaultMessage: 'Admin' });
  const breadcrumbPortfolios = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_edit.breadcrumbs.portfolios.label', defaultMessage: 'Portfolios' });
  const breadcrumbEditPortfolio = intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_edit.breadcrumbs.update_portfolio.label', defaultMessage: 'Edit Portfolio' });
  const mutationName = 'editPortfolio';
  // start: form submit functionalities
  const handleEditPortfolioSuccess = (response, errors) => {
    setProcessing(false);

    if (errors && errors.length > 0) {
      errors.forEach((error) => FlashesStore.flash(FlashesStore.ERROR, {
        message: FlashesStore.ERROR,
        source: {
          errors: [error],
        },
      }, mutationName));
      return;
    }

    const { uuid } = response.updatePortfolio;

    FlashesStore.flash(
      FlashesStore.SUCCESS,
      <span>
        {intl.formatMessage({ id: 'admin.admin_portfolio.admin_portfolio_edit.response_message.success', defaultMessage: 'Successfully updated portfolio {uuid}.' }, { uuid })}
      </span>,
      mutationName,
    );

    router.push(`/admin/portfolios/${uuid}`);
  };

  const handleEditPortfolioFailure = (error) => {
    setProcessing(false);

    FlashesStore.flash(FlashesStore.ERROR, error, mutationName);
  };

  const handleEditPortfolio = (title) => {
    setProcessing(true);

    editPortfolio(
      portfolioUuid,
      { title },
      handleEditPortfolioSuccess,
      handleEditPortfolioFailure,
    );
  };

  // end: form submit functionalities

  return (
    <>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{pageTitle}</title>
      </Helmet>

      <Breadcrumbs
        breadcrumbs={[
          { name: breadcrumbAdmin, path: '/admin' },
          { name: breadcrumbPortfolios, path: '/admin/portfolios' },
          { name: portfolioTitle, path: `/admin/portfolios/${portfolioUuid}` },
          { name: breadcrumbEditPortfolio },
        ]}
      />
      <Form
        handleEditPortfolio={handleEditPortfolio}
        processing={processing}
        portfolioTitle={portfolioTitle}
      />
    </>
  );
}
export default AdminPortfolioEdit;

AdminPortfolioEdit.propTypes = {
  router: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  portfolio: PropTypes.object, // eslint-disable-line react/forbid-prop-types
};
